import React from 'react'
import { useNavigate } from 'react-router-dom'
import { isAfter } from 'date-fns'
import { Button, Columns, Element, Icon } from 'react-bulma-components'
import { Mission } from '../../api/missions'
import { WorkPeriod } from '../../api/work-periods'
import { useValidateMutation, useRemoveWorkPeriodMutation } from '../../queries/work-periods'
import { useUnAbandonMutation, useUpdateMutationKey } from '../../queries/missions'
import { MissionAbandonedIcon, DeleteIcon } from '../icons'
import { useModal } from '../modal'
import ButtonWithTooltip from '../button-with-tooltip/button-with-tooltip'
import { RequestButton, RequestMessage } from '../request-components/request-components'
import { WorkPeriodAbandonedMessage, WorkPeriodValidatedMessage } from './work-period-messages'
import WorkPeriodValidateButton from './validate-button'
import AbandonConfirmation from './modals/abandon-confirmation'
import ConfirmationModal from '../confirmation-modal/confirmation-modal'
import { MissionAbandonedMessage } from '../missions/mission-messages'
import { useMutationState } from '../../queries'
import { isAuthorizedFor } from '../protected/protected'
import { hasWorkPeriodWithClockingInProgress } from '../../../../backend/src/utils/missions'
import WorkerLabel from '../texts/worker-label'
import ResourceActions from '../resources/resource-actions'

interface WorkPeriodActionsProps {
  mission: Mission
  workPeriod: WorkPeriod
}

const WorkPeriodActions: React.FC<WorkPeriodActionsProps> = ({ mission, workPeriod }) => {
  const actions = useActions(workPeriod, mission)
  return <ResourceActions actions={actions} />
}

export default WorkPeriodActions

interface Action {
  title: string
  description?: string
  content: React.ReactNode
}

/**
 * Determine if a mission can be abandoned on the client side.
 * Mirrors backend logic to prevent UI desyncs.
 * @param mission Full mission object including status flags and work periods
 * @param workPeriods Optional work periods list if already fetched elsewhere
 * @returns true when the mission is eligible for abandonment
 */
// TODO: duplicate of backend check (found  a solution to avoid duplicate code)
const canMissionBeAbandoned = (mission: Mission, workPeriods?: WorkPeriod[]): boolean => {
  // Block when mission already validated, abandoned or cancelled
  if (mission.isValidated || mission.isAbandoned || mission.isCancelled) {
    return false
  }

  // Use provided work periods, fall back to mission payload or empty list
  const workPeriodsToCheck = workPeriods || mission.workPeriods || []
  const now = new Date()

  // Filter future periods only
  const nextWorkPeriods = workPeriodsToCheck.filter(wP => isAfter(new Date(wP.start.date), now))

  // If future periods exist, ensure all are abandonable
  if (
    nextWorkPeriods.length > 0 &&
    nextWorkPeriods.some(wP => wP.__actions?.canBeAbandoned === false)
  ) {
    return false
  }

  // All checks passed, mission is abandonable
  return true
}

const useActions = (workPeriod: WorkPeriod, mission: Mission): Action[] => {
  //Mutations
  const navigate = useNavigate()
  const validateWorkPeriodMutation = useValidateMutation()
  const removeWorkPeriodMutation = useRemoveWorkPeriodMutation()
  const missionUpdateMutationState = useMutationState(useUpdateMutationKey())
  const unAbandonMissionMutation = useUnAbandonMutation()
  const modal = useModal()
  const removeModal = useModal()
  const missionCanBeAbandoned = canMissionBeAbandoned(mission)
  return React.useMemo(
    () =>
      [
        isAuthorizedFor(['employerMember'], {
          resource: 'missions',
          name: 'validateWorkPeriod',
        }) && {
          title: 'Valider la Journée de Travail',
          description:
            'Une fois validée, la Journée de Travail ne peut plus être modifiée et plus aucun pointage ne sera pris en compte. Cette action est réversible.',
          content: (
            <Columns>
              {workPeriod.__actions.canBeInvalidated && (
                <Columns.Column>
                  <WorkPeriodValidatedMessage workPeriod={workPeriod} />
                </Columns.Column>
              )}
              <Columns.Column narrow>
                <WorkPeriodValidateButton
                  workPeriod={workPeriod}
                  label="Valider"
                  cancelLabel="Annuler"
                  type="button"
                  onClick={() =>
                    validateWorkPeriodMutation.mutate({
                      workPeriodId: workPeriod._id,
                      isValidated: !workPeriod.isValidated,
                    })
                  }
                />
              </Columns.Column>
            </Columns>
          ),
        },
        isAuthorizedFor(['employerMember'], { resource: 'missions', name: 'abandon' }) && {
          title: 'Abandonner la Mission à partir de ce jour',
          description: (
            <>
              Le <WorkerLabel /> ne pourra plus pointer aucune entrée et sortie pour cette Journée
              de Travail et le restant de la Mission.\ Il sera remis à disposition de l'Agence
              d'Intérim.
            </>
          ),
          content: (
            <>
              <Columns>
                {workPeriod.isAbandoned && (
                  <>
                    <Columns.Column>
                      <WorkPeriodAbandonedMessage workPeriod={workPeriod} />
                      {/* @ts-ignore */}
                      <RequestMessage mutation={missionUpdateMutationState} />
                    </Columns.Column>
                    {mission.__actions.canBeUnAbandoned && (
                      <Columns.Column narrow>
                        <RequestButton
                          textColor="grey"
                          mutation={unAbandonMissionMutation}
                          onClick={() =>
                            unAbandonMissionMutation.mutate({ missionId: mission._id })
                          }
                        >
                          <Icon.Text>
                            <Icon>
                              <MissionAbandonedIcon />
                            </Icon>
                            <Element renderAs="span">Annuler</Element>
                          </Icon.Text>
                        </RequestButton>
                      </Columns.Column>
                    )}
                  </>
                )}
                {!workPeriod.isAbandoned && mission.isAbandoned && (
                  <>
                    <Columns.Column>
                      <MissionAbandonedMessage mission={mission} />
                    </Columns.Column>
                  </>
                )}
                <Columns.Column narrow>
                  <Button.Group>
                    {!workPeriod.isAbandoned &&
                      !mission.isAbandoned &&
                      workPeriod.__actions.canBeAbandoned && (
                        <ButtonWithTooltip
                          type="button"
                          color="danger"
                          onClick={() => modal.setIsDisplayed(true)}
                          disabled={!missionCanBeAbandoned}
                          tooltip={{
                            text: hasWorkPeriodWithClockingInProgress(mission)
                              ? "Une journée de travail a un pointage d'entrée mais pas de sortie"
                              : 'Mission interrompue ou Journée de Travail Validée',
                            show: !missionCanBeAbandoned,
                            id: `${mission._id}-cancel-abandonment`,
                          }}
                        >
                          <Icon mr={1}>
                            <MissionAbandonedIcon />
                          </Icon>
                          {'Abandon'}
                        </ButtonWithTooltip>
                      )}
                  </Button.Group>
                </Columns.Column>
              </Columns>
              <AbandonConfirmation
                show={modal.isDisplayed}
                hide={() => modal.setIsDisplayed(false)}
                mission={mission}
                workPeriod={workPeriod}
              />
            </>
          ),
        },
        isAuthorizedFor(['employerMember'], { resource: 'missions', name: 'deleteWorkPeriod' }) &&
          workPeriod.__actions.canBeRemoved && {
            title: 'Supprimer la journée de travail',
            description:
              "La journée de travail sera définitivement supprimée de la mission. Cette action est possible uniquement s'il n'y a aucun pointage, aucun événement, et que la journée n'est pas validée.",
            content: (
              <>
                <Button
                  color="danger"
                  onClick={() => removeModal.setIsDisplayed(true)}
                  data-test="remove-work-period-button"
                >
                  <Icon mr={1}>
                    <DeleteIcon />
                  </Icon>
                  Supprimer la journée
                </Button>
                <ConfirmationModal
                  show={removeModal.isDisplayed}
                  onCancel={() => removeModal.setIsDisplayed(false)}
                  onConfirm={() => {
                    removeWorkPeriodMutation.mutate(
                      { missionId: mission._id, workPeriodId: workPeriod._id },
                      {
                        onSuccess: () => {
                          removeModal.setIsDisplayed(false)
                          navigate(`/missions/${mission._id}`)
                        },
                      },
                    )
                  }}
                  confirmButtonLabel="Supprimer"
                  confirmButtonProps={{
                    disabled: removeWorkPeriodMutation.isLoading,
                  }}
                >
                  <Element>
                    Êtes-vous sûr de vouloir supprimer cette journée de travail ? Elle sera
                    définitivement retirée de la mission.
                  </Element>
                </ConfirmationModal>
              </>
            ),
          },
      ].filter(v => v) as Action[],
    [
      mission,
      workPeriod,
      validateWorkPeriodMutation,
      missionCanBeAbandoned,
      removeWorkPeriodMutation,
      navigate,
    ],
  )
}
